import React, { useState } from 'react';
import { useConfiguration } from '../../../contexts/ConfigurationContext';
import InstructionPanel from '../../../components/ui/InstructionPanel/InstructionPanel';
import { Icon } from '../../../components/ui/Icon';
import { Button } from '../../../components/ui/Button2';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Switch } from '../../../components/ui/Switch';
import { getProgressColor } from '../../../utils/progressColor';
import styles from './CALCriteriaEditor.module.css';

const CALCriteriaEditor = () => {
  const { config, updateConfiguration } = useConfiguration();
  const [selectedLevel, setSelectedLevel] = useState('CAL1');
  const [expandedSections, setExpandedSections] = useState({
    settings: true,
    editor: true
  });

  const calLevels = ['CAL1', 'CAL2', 'CAL3', 'CAL4'];
  const calConfig = config?.configuration?.cal_criteria_config;

  // Get current definition for selected level
  const getCurrentDefinition = () => {
    if (!calConfig) return null;
    return calConfig.definitions.find(d => d.cal_level === selectedLevel);
  };

  // Toggle custom criteria on/off
  const handleToggleCustom = (enabled) => {
    const updatedConfig = {
      ...config,
      configuration: {
        ...config.configuration,
        cal_criteria_config: {
          ...calConfig,
          use_custom_definitions: enabled
        }
      }
    };
    updateConfiguration(updatedConfig);
  };

  // Update criteria text
  const handleCriteriaChange = (field, value) => {
    const currentDef = getCurrentDefinition();
    const definitions = calConfig.definitions.filter(d => d.cal_level !== selectedLevel);

    const updatedDef = {
      ...currentDef,
      cal_level: selectedLevel,
      [field]: value
    };

    definitions.push(updatedDef);

    const updatedConfig = {
      ...config,
      configuration: {
        ...config.configuration,
        cal_criteria_config: {
          ...calConfig,
          definitions
        }
      }
    };
    updateConfiguration(updatedConfig);
  };

  // Add example to list
  const handleAddExample = (exampleType) => {
    const currentDef = getCurrentDefinition() || {
      cal_level: selectedLevel,
      criteria: '',
      control_requirements: '',
      verification_requirements: '',
      control_examples: [],
      requirement_examples: [],
      iso_reference: ''
    };

    const updatedExamples = [...(currentDef[exampleType] || []), ''];
    handleCriteriaChange(exampleType, updatedExamples);
  };

  // Remove example from list
  const handleRemoveExample = (exampleType, index) => {
    const currentDef = getCurrentDefinition();
    const updatedExamples = currentDef[exampleType].filter((_, i) => i !== index);
    handleCriteriaChange(exampleType, updatedExamples);
  };

  // Update specific example
  const handleExampleChange = (exampleType, index, value) => {
    const currentDef = getCurrentDefinition();
    const updatedExamples = [...currentDef[exampleType]];
    updatedExamples[index] = value;
    handleCriteriaChange(exampleType, updatedExamples);
  };

  const currentDef = getCurrentDefinition();
  const completeness = calConfig
    ? (calConfig.definitions.length / 4) * 100
    : 0;

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <h2>CAL Criteria Definition</h2>
      </div>

      {/* Global Settings Section */}
      <div className={styles.section}>
        <button
          className={styles.sectionHeader}
          onClick={() => setExpandedSections(prev => ({ ...prev, settings: !prev.settings }))}
        >
          <span>Global Settings</span>
          <Icon name={expandedSections.settings ? 'chevron-down' : 'chevron-right'} size={16} />
        </button>

        {expandedSections.settings && (
          <div className={styles.sectionContent}>
            {/* Toggle Custom Criteria */}
            <div className={styles.settingRow}>
              <div className={styles.settingInfo}>
                <span className={styles.settingLabel}>Use Custom CAL Criteria Definitions</span>
                <p className={styles.settingHelp}>
                  When enabled, AI agents will use your custom criteria instead of ISO 21434 defaults.
                </p>
              </div>
              <Switch
                checked={calConfig?.use_custom_definitions || false}
                onCheckedChange={handleToggleCustom}
                aria-label="Use custom CAL criteria definitions"
              />
            </div>

            {/* Completeness Indicator */}
            {calConfig?.use_custom_definitions && (
              <div className={styles.completenessRow}>
                <span className={styles.completenessLabel}>Definition Completeness:</span>
                <div className={styles.progressBar}>
                  <div
                    className={styles.progressFill}
                    style={{
                      width: `${completeness}%`,
                      backgroundColor: getProgressColor(completeness, { scheme: 'completeness' })
                    }}
                  />
                </div>
                <span className={styles.completenessCount}>
                  {calConfig.definitions.length}/4 CAL levels defined
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Criteria Editor Section */}
      {calConfig?.use_custom_definitions && (
        <div className={styles.section}>
          <button
            className={styles.sectionHeader}
            onClick={() => setExpandedSections(prev => ({ ...prev, editor: !prev.editor }))}
          >
            <span>Criteria Editor</span>
            <Icon name={expandedSections.editor ? 'chevron-down' : 'chevron-right'} size={16} />
          </button>

          {expandedSections.editor && (
            <div className={styles.sectionContent}>
              {/* CAL Level Selector */}
              <div className={styles.selectorGroup}>
                <label className={styles.selectorLabel}>Select CAL Level:</label>
                <div className={styles.buttonGrid}>
                  {calLevels.map(level => (
                    <button
                      key={level}
                      className={`${styles.selectorButton} ${selectedLevel === level ? styles.selected : ''}`}
                      onClick={() => setSelectedLevel(level)}
                    >
                      {level}
                      {calConfig.definitions.find(d => d.cal_level === level) && (
                        <Icon name="check" size={12} className={styles.definedBadge} />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Criteria */}
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>
                    Criteria <span className={styles.required}>*</span>
                  </label>
                  <Textarea
                    value={currentDef?.criteria || ''}
                    onChange={(e) => handleCriteriaChange('criteria', e.target.value)}
                    placeholder="What qualifies for this CAL level? (10-2000 characters)"
                    minLength={10}
                    maxLength={2000}
                    rows={4}
                  />
                  <span className={styles.charCount}>
                    {currentDef?.criteria?.length || 0}/2000 characters
                  </span>
                </div>

                {/* Control Requirements */}
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>
                    Control Requirements <span className={styles.required}>*</span>
                  </label>
                  <Textarea
                    value={currentDef?.control_requirements || ''}
                    onChange={(e) => handleCriteriaChange('control_requirements', e.target.value)}
                    placeholder="Types and strength of controls required (e.g., 'AES-256 encryption, HMAC-SHA256')"
                    minLength={10}
                    maxLength={2000}
                    rows={4}
                  />
                  <span className={styles.charCount}>
                    {currentDef?.control_requirements?.length || 0}/2000 characters
                  </span>
                </div>

                {/* Verification Requirements */}
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>
                    Verification Requirements <span className={styles.required}>*</span>
                  </label>
                  <Textarea
                    value={currentDef?.verification_requirements || ''}
                    onChange={(e) => handleCriteriaChange('verification_requirements', e.target.value)}
                    placeholder="Testing and validation methods required (e.g., 'penetration testing, formal verification')"
                    minLength={10}
                    maxLength={2000}
                    rows={4}
                  />
                  <span className={styles.charCount}>
                    {currentDef?.verification_requirements?.length || 0}/2000 characters
                  </span>
                </div>

                {/* Control Examples */}
                <div className={styles.fieldGroup}>
                  <div className={styles.examplesHeader}>
                    <label className={styles.fieldLabel}>
                      Control Examples (Optional, max 10)
                    </label>
                    <Button
                      variant="outline"
                      size="xs"
                      startIcon={<Icon name="add" size={14} />}
                      onClick={() => handleAddExample('control_examples')}
                      disabled={(currentDef?.control_examples?.length || 0) >= 10}
                    >
                      Add Example
                    </Button>
                  </div>
                  {currentDef?.control_examples?.map((example, index) => (
                    <div key={index} className={styles.exampleRow}>
                      <span className={styles.exampleNumber}>{index + 1}</span>
                      <Input
                        type="text"
                        className={styles.exampleInput}
                        value={example}
                        onChange={(e) => handleExampleChange('control_examples', index, e.target.value)}
                        placeholder={`Control example ${index + 1}`}
                      />
                      <Button
                        variant="destructive"
                        size="icon-sm"
                        onClick={() => handleRemoveExample('control_examples', index)}
                        title="Remove example"
                        aria-label="Remove example"
                      >
                        <Icon name="close" size={14} />
                      </Button>
                    </div>
                  ))}
                </div>

                {/* Requirement Examples */}
                <div className={styles.fieldGroup}>
                  <div className={styles.examplesHeader}>
                    <label className={styles.fieldLabel}>
                      Requirement Examples (Optional, max 10)
                    </label>
                    <Button
                      variant="outline"
                      size="xs"
                      startIcon={<Icon name="add" size={14} />}
                      onClick={() => handleAddExample('requirement_examples')}
                      disabled={(currentDef?.requirement_examples?.length || 0) >= 10}
                    >
                      Add Example
                    </Button>
                  </div>
                  {currentDef?.requirement_examples?.map((example, index) => (
                    <div key={index} className={styles.exampleRow}>
                      <span className={styles.exampleNumber}>{index + 1}</span>
                      <Input
                        type="text"
                        className={styles.exampleInput}
                        value={example}
                        onChange={(e) => handleExampleChange('requirement_examples', index, e.target.value)}
                        placeholder={`Requirement example ${index + 1}`}
                      />
                      <Button
                        variant="destructive"
                        size="icon-sm"
                        onClick={() => handleRemoveExample('requirement_examples', index)}
                        title="Remove example"
                        aria-label="Remove example"
                      >
                        <Icon name="close" size={14} />
                      </Button>
                    </div>
                  ))}
                </div>

                {/* ISO Reference */}
                <div className={styles.fieldGroup}>
                  <label className={styles.fieldLabel}>
                    ISO/SAE 21434 Reference (Optional)
                  </label>
                  <Input
                    type="text"
                    value={currentDef?.iso_reference || ''}
                    onChange={(e) => handleCriteriaChange('iso_reference', e.target.value)}
                    placeholder="e.g., ISO 21434 Table 3 - CAL level CAL1"
                    maxLength={500}
                  />
                </div>
            </div>
          )}
        </div>
      )}

      {/* Help Section */}
      <InstructionPanel title="How to Use">
        <ul>
          <li>Enable "Use Custom CAL Criteria Definitions" to override system defaults</li>
          <li>Define all 4 CAL levels (CAL1, CAL2, CAL3, CAL4) for 100% completeness</li>
          <li>Criteria should describe what qualifies a risk for that CAL level</li>
          <li>Control Requirements define the types/strength of controls needed</li>
          <li>Verification Requirements specify testing/validation methods</li>
          <li>Examples help AI agents generate specific, compliant outputs</li>
          <li>Click "Save Changes" to create a new configuration version</li>
        </ul>
      </InstructionPanel>
    </div>
  );
};

export default CALCriteriaEditor;
